import { useMemo, useState } from 'react';
import FeatherIcon from 'feather-icons-react';

export function flatToCards(flat) {
  return [
    {
      id: 'full-vlgc',
      title: 'Full Cargo VLGC',
      enabled: flat.isFullCargoVLGC ?? false,
      frequency: flat.isFullCargoVLGC
        ? (flat.fullCargoVLGCFrequency ?? '')
        : null,
      propane: {
        min: flat.isFullCargoVLGC
          ? (flat.fullCargoVLGCRefrigratePropaneMin ?? '')
          : null,
        max: flat.isFullCargoVLGC
          ? (flat.fullCargoVLGCRefrigratePropaneMax ?? '')
          : null,
      },
      butane: {
        min: flat.isFullCargoVLGC
          ? (flat.fullCargoVLGCRefrigrateButaneMin ?? '')
          : null,
        max: flat.isFullCargoVLGC
          ? (flat.fullCargoVLGCRefrigrateButaneMax ?? '')
          : null,
      },
    },
    {
      id: 'half-vlgc',
      title: 'Half Cargo VLGC',
      enabled: flat.isHalfCargoVLGC ?? false,
      frequency: flat.isHalfCargoVLGC
        ? (flat.halfCargoVLGCFrequency ?? null)
        : null,
      propane: {
        min: flat.isHalfCargoVLGC
          ? (flat.halfCargoVLGCRefrigratePropaneMin ?? null)
          : null,
        max: flat.isHalfCargoVLGC
          ? (flat.halfCargoVLGCRefrigratePropaneMax ?? null)
          : null,
      },
      butane: {
        min: flat.isHalfCargoVLGC
          ? (flat.halfCargoVLGCRefrigrateButaneMin ?? null)
          : null,
        max: flat.isHalfCargoVLGC
          ? (flat.halfCargoVLGCRefrigrateButaneMax ?? null)
          : null,
      },
    },
    {
      id: 'semi-refregerated',
      title: 'Semi Refregerated',
      enabled: flat.isSemiRefrigerated ?? false,
      frequency: flat.isSemiRefrigerated
        ? (flat.semiRefrigeratedFrequency ?? null)
        : null,
      propane: {
        min: flat.isSemiRefrigerated
          ? (flat.semiRefrigeratedRefrigratePropaneMin ?? null)
          : null,
        max: flat.isSemiRefrigerated
          ? (flat.semiRefrigeratedRefrigratePropaneMax ?? null)
          : null,
      },
      butane: {
        min: flat.isSemiRefrigerated
          ? (flat.semiRefrigeratedRefrigrateButaneMin ?? null)
          : null,
        max: flat.isSemiRefrigerated
          ? (flat.semiRefrigeratedRefrigrateButaneMax ?? null)
          : null,
      },
    },
  ];
}

export function cardsToFlat(cards) {
  const find = id => cards.find(c => c.id === id) ?? {};
  const full = find('full-vlgc');
  const half = find('half-vlgc');
  const semi = find('semi-refregerated');

  return {
    isFullCargoVLGC: full.enabled ?? false,
    fullCargoVLGCFrequency: full.frequency ?? null,
    fullCargoVLGCRefrigratePropaneMin: full.propane?.min ?? null,
    fullCargoVLGCRefrigratePropaneMax: full.propane?.max ?? null,
    fullCargoVLGCRefrigrateButaneMin: full.butane?.min ?? null,
    fullCargoVLGCRefrigrateButaneMax: full.butane?.max ?? null,
    isHalfCargoVLGC: half.enabled ?? false,
    halfCargoVLGCFrequency: half.frequency ?? null,
    halfCargoVLGCRefrigratePropaneMin: half.propane?.min ?? null,
    halfCargoVLGCRefrigratePropaneMax: half.propane?.max ?? null,
    halfCargoVLGCRefrigrateButaneMin: half.butane?.min ?? null,
    halfCargoVLGCRefrigrateButaneMax: half.butane?.max ?? null,
    isSemiRefrigerated: semi.enabled ?? false,
    semiRefrigeratedFrequency: semi.frequency ?? null,
    semiRefrigeratedRefrigratePropaneMin: semi.propane?.min ?? null,
    semiRefrigeratedRefrigratePropaneMax: semi.propane?.max ?? null,
    semiRefrigeratedRefrigrateButaneMin: semi.butane?.min ?? null,
    semiRefrigeratedRefrigrateButaneMax: semi.butane?.max ?? null,
  };
}

function Checkbox({ checked, onChange, disabled }) {
  return (
    <button
      type='button'
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      aria-pressed={checked}
      className={`btn p-0 d-inline-flex align-items-center justify-content-center rounded-circle border ${
        checked ? 'bg-secondary border-secondary text-white' : 'bg-white border'
      }`}
      style={{
        width: 20,
        height: 20,
        minWidth: 20,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {checked && <FeatherIcon icon='check' size={13} />}
    </button>
  );
}

function Stepper({ value, onChange, min = 0, disabled }) {
  return (
    <input
      type='number'
      min={min}
      value={value ?? ''}
      onChange={
        disabled
          ? undefined
          : e => {
              const v = e.target.value;
              onChange(v === '' ? min : Math.max(min, Number(v)));
            }
      }
      disabled={disabled}
      className='form-control form-control-sm text-center'
      style={{ width: 90, height: 36, opacity: disabled ? 0.5 : 1 }}
    />
  );
}

function NumberField({ label, value, onChange, unit = 'MT', disabled }) {
  return (
    <div
      className='d-flex align-items-center border rounded bg-white'
      style={{ height: 36 }}
    >
      <span className='small text-muted ps-2 pe-1'>{label}</span>
      <input
        type='number'
        step='0.001'
        value={value}
        onChange={
          disabled
            ? undefined
            : e => onChange(e.target.value === '' ? '' : Number(e.target.value))
        }
        disabled={disabled}
        className='form-control border-0 bg-transparent shadow-none px-1'
        style={{
          minWidth: 0,
          flex: '1 1 0%',
          height: '100%',
          MozAppearance: 'textfield',
          WebkitAppearance: 'textfield',
          opacity: disabled ? 0.5 : 1,
        }}
      />
      <span className='small text-muted pe-2 ps-1 flex-shrink-0'>{unit}</span>
    </div>
  );
}

function RefrigerateGroup({ title, data, onChange, error, disabled }) {
  return (
    <div className='flex-fill border rounded p-3 bg-white'>
      <div className='small fw-medium text-muted mb-2'>{title}</div>
      <div className='d-flex flex-column gap-2'>
        <NumberField
          label='Min'
          value={data.min}
          onChange={v => onChange({ ...data, min: v })}
          disabled={disabled}
        />
        <NumberField
          label='Max'
          value={data.max}
          onChange={v => onChange({ ...data, max: v })}
          disabled={disabled}
        />
      </div>
      {error && (
        <div className='text-danger d-flex align-items-center gap-1 mt-1'>
          <i className='mdi mdi-alert-circle-outline'></i>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function CargoCard({
  card,
  onChange,
  groupErrors,
  comptitle,
  sectitle,
  isProcurementCompleted,
  qtyDeliveryData,
}) {
  const update = patch => onChange({ ...card, ...patch });

  return (
    <div
      className='border rounded bg-light p-3'
      style={{ minWidth: 260, flex: '1 1 0%' }}
    >
      <div className='d-flex align-items-center gap-2 mb-3'>
        <Checkbox
          checked={card.enabled}
          onChange={() => update({ enabled: !card.enabled })}
          disabled={
            isProcurementCompleted !== false ||
            qtyDeliveryData?.items?.length > 0
          }
        />
        <span className='small fw-semibold text-dark'>{card.title}</span>
      </div>

      <div
        className={`d-flex flex-column gap-2 ${
          card.enabled ? '' : 'pe-none opacity-50'
        }`}
      >
        <div className='d-flex align-items-center justify-content-between border rounded bg-white px-3 py-2'>
          <span className='small text-muted'>Frequency</span>
          <Stepper
            value={card.frequency}
            onChange={v => update({ frequency: v })}
            disabled={
              isProcurementCompleted !== false ||
              qtyDeliveryData?.items?.length > 0
            }
          />
        </div>

        <div className='d-flex gap-2'>
          <RefrigerateGroup
            title={comptitle}
            data={card.propane}
            onChange={propane => update({ propane })}
            error={groupErrors[`${card.id}-propane`]}
            disabled={
              isProcurementCompleted !== false ||
              qtyDeliveryData?.items?.length > 0
            }
          />
          <RefrigerateGroup
            title={sectitle}
            data={card.butane}
            onChange={butane => update({ butane })}
            error={groupErrors[`${card.id}-butane`]}
            disabled={
              isProcurementCompleted !== false ||
              qtyDeliveryData?.items?.length > 0
            }
          />
        </div>
      </div>
    </div>
  );
}

export default function CargoTypeComposition({
  form,
  setForm,
  comptitle,
  sectitle,
  qtyDeliveryData,
}) {
  const cards = useMemo(() => flatToCards(form), [form]);
  const [groupErrors, setGroupErrors] = useState({});

  const validateGroup = (cardId, groupType, min, max) => {
    const key = `${cardId}-${groupType}`;
    const minVal = min === '' || min == null ? null : Number(min);
    const maxVal = max === '' || max == null ? null : Number(max);

    if (minVal != null && maxVal != null && minVal > maxVal) {
      setGroupErrors(prev => ({
        ...prev,
        [key]: 'Min cannot be greater than Max',
      }));
    } else {
      setGroupErrors(prev => {
        // eslint-disable-next-line no-unused-vars
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const updateCard = updated => {
    if (updated.propane) {
      validateGroup(
        updated.id,
        'propane',
        updated.propane.min,
        updated.propane.max
      );
    }
    if (updated.butane) {
      validateGroup(
        updated.id,
        'butane',
        updated.butane.min,
        updated.butane.max
      );
    }

    const reset =
      updated.enabled === false
        ? {
            frequency: null,
            propane: { min: null, max: null },
            butane: { min: null, max: null },
          }
        : {};
    const finalCard = { ...updated, ...reset };
    const newCards = cards.map(c => (c.id === finalCard.id ? finalCard : c));
    const flat = cardsToFlat(newCards);
    setForm(prev => ({ ...prev, ...flat }));
  };

  return (
    <div>
      <div>
        <div className='d-flex flex-row flex-nowrap gap-3 overflow-auto pb-2'>
          {cards.map(card => (
            <CargoCard
              key={card.id}
              card={card}
              onChange={updateCard}
              groupErrors={groupErrors}
              comptitle={comptitle || sectitle}
              sectitle={sectitle}
              isProcurementCompleted={form.isProcurementCompleted}
              qtyDeliveryData={qtyDeliveryData}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
